import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatMoney } from '../../lib/nexusPhone';
import NexusPhoneNumberModal from '../settings/NexusPhoneNumberModal';

const REFRESH_EVENT = 'nexus-phone-balance-refresh';

type PhoneBalance = {
  balanceCents: number;
  currency: string;
  /** Number is suspended because the wallet hit $0. */
  pausedForFunds: boolean;
  /** Last auto top-up charge failed, so the wallet will not refill on its own. */
  paymentFailed: boolean;
};

/**
 * Quiet phone-balance line for businesses on a Nexus number.
 * Stays out of the way while the balance is healthy. When the balance is $0
 * or the card charge failed, it says so — calls and texts stop for money,
 * not because messaging is broken.
 *
 * `alertOnly` renders nothing unless that money problem is present, so a
 * conversation header can stay untouched the rest of the time.
 */
export default function NexusPhoneBalanceNotice({
  businessId,
  alertOnly = false,
}: {
  businessId: string | undefined | null;
  alertOnly?: boolean;
}) {
  const [balance, setBalance] = useState<PhoneBalance | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!businessId || !supabase) {
      setBalance(null);
      return;
    }
    const [numberRes, walletRes] = await Promise.all([
      supabase
        .from('business_nexus_phone_numbers')
        .select('status, suspended_reason')
        .eq('business_id', businessId)
        .neq('status', 'released')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('nexus_phone_wallets')
        .select('balance_cents, currency, auto_topup_paused_reason')
        .eq('business_id', businessId)
        .maybeSingle(),
    ]);
    if (numberRes.error || walletRes.error || !numberRes.data || !walletRes.data) {
      setBalance(null);
      return;
    }
    const wallet = walletRes.data;
    setBalance({
      balanceCents: Number(wallet.balance_cents) || 0,
      currency: (wallet.currency as string | null) || 'usd',
      pausedForFunds: numberRes.data.status === 'suspended' && numberRes.data.suspended_reason === 'no_funds',
      paymentFailed: wallet.auto_topup_paused_reason === 'payment_failed',
    });
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => void load();
    window.addEventListener(REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(REFRESH_EVENT, onRefresh);
  }, [load]);

  if (!balance) return null;

  const empty = balance.balanceCents <= 0 || balance.pausedForFunds;
  const problem = empty || balance.paymentFailed;
  if (alertOnly && !problem) return null;

  const amount = formatMoney(balance.balanceCents, balance.currency);
  let text: string;
  if (empty && balance.paymentFailed) {
    text = `Phone balance is ${amount}. The last card charge didn't go through, so calls and texts are paused.`;
  } else if (empty) {
    text = `Phone balance is ${amount}, so calls and texts are paused until you add funds.`;
  } else if (balance.paymentFailed) {
    text = `Phone balance ${amount}. Auto top-up didn't go through — calls and texts keep working until it hits $0.`;
  } else {
    text = `Phone balance ${amount}`;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          problem
            ? `${alertOnly ? 'mx-3 mt-2 mb-1 block' : 'mt-2 block w-full'} text-left rounded-lg bg-amber-50 px-2.5 py-1.5 text-[12px] leading-snug text-amber-900 hover:bg-amber-100`
            : 'mt-2 text-left text-[11px] leading-none text-gray-400 hover:text-gray-600'
        }
      >
        {text}
      </button>
      <NexusPhoneNumberModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onChanged={() => {
          void load();
          window.dispatchEvent(new Event(REFRESH_EVENT));
        }}
      />
    </>
  );
}
